/*
 * Copyright (c) 2019. Nedaware SL
 */

/**
 * Nedaware S.L.
 * @author David Campos Rodríguez <david.campos.r96@gmail.com>
 */

import {FilterInterface, InvalidFilterProperty, InvalidFilterValue} from "../../api/filter-interface";
import {InternalPropertyMap} from "../basic-entity-interface/mapping-internal";
import {Resource} from "../../api/resource";
import {Uri} from "../../api/uri";

/**
 * The multiple search filter admits both a single value and several values,
 * this filter sends the parameters to be filtered to the back-end as text.
 * Notice that for non-string types there might be another better filters than this one.
 * Accepted values are:
 *      - Instances of Model
 *      - Instances of Uri
 *      - strings
 *      - numbers
 *      - Values which can be casted to the previously listed ones
 * This is the way the properties will be sent:
 *      - Uri and Model objects are sent as IRIs
 *      - The other objects are casted as if sent to the API in the body of a request
 * Notice some search types from ApiPlatform, such as "partial", "start", etc. only
 * accept a single item (never several). For these cases SimpleSearchFilter should be used
 * @see SimpleSearchFilter
 * @see NumericFilter
 */
export class MultipleSearchFilter implements FilterInterface {
    /**
     * Casts the value of a property to be sent
     */
    private static _sCasting(mapping: InternalPropertyMap, value: any): string {
        if (value instanceof Resource) {
            return value.iri.toString();
        } else if (value instanceof Uri) {
            return value.toString();
        }

        if (mapping.caster) {
            value = mapping.caster.fromModel(value);
        }

        if (value == null) {
            return null;
        } else if (typeof value === "string") {
            return value;
        } else if (typeof value === "number") {
            return value.toString(10);
        } else {
            throw new InvalidFilterValue(value, "the value could not be casted to a string by the filter.");
        }
    }

    dataToParams(property: InternalPropertyMap, data: any[] | any): { [p: string]: string | string[] } {
        if (property == null) {
            throw new InvalidFilterProperty(property, "the property cannot be null");
        }
        const params = {};
        let param = property.paramName;
        if (data instanceof Array) {
            if (data.length > 1) {
                param += '[]';
            }
            params[param] = data.map(datum => MultipleSearchFilter._sCasting(property, datum));
        } else {
            params[param] = MultipleSearchFilter._sCasting(property, data);
        }
        return params;
    }
}

/**
 * The simple search filter is a variation of the multiple search filter which accepts,
 * exclusively, a single item.
 */
export class SimpleSearchFilter extends MultipleSearchFilter {
    dataToParams(property: InternalPropertyMap, data: any[] | any): { [p: string]: string | string[] } {
        if (Array.isArray(data)) {
            throw new Error('The simple search filter admits only single values');
        }
        return super.dataToParams(property, data);
    }
}

export class StartSearchFilter extends SimpleSearchFilter {
}

export class WordStartSearchFilter extends SimpleSearchFilter {
}

export class PartialSearchFilter extends SimpleSearchFilter {
}

export class ExactSearchFilter extends MultipleSearchFilter {
}

export class EndSearchFilter extends SimpleSearchFilter {
}
